<!--
  @component IdentityIO — the consumer face's identity provider.
  tier:      face wrapper (sits ABOVE the deployment IO face for
             the consumer; admin doesn't use this).
  shape:     own KEY + Context + useIdentityIO() hook (the
             one-contract two-payoffs discipline).
  publishes: IDENTITY_IO_KEY → IdentityIOContext → useIdentityIO()

  WHAT THIS IS — and isn't
  ─────────────────────────────────────────────────────────────────

  A MOCK auth + project-tenancy context. Designed with the same
  FACE-LEDGER discipline as the deployment IO node so a real
  identity adapter (a future `@liquidefy/security` accounts node
  or similar) can swap in transparently later.

  Today (C0):
    - signIn() accepts any email; synthesises a User stamped
      "mock-…". Persists to localStorage so the session survives
      reload.
    - createProject() synthesises a Project with a generated id
      and appends it to the user's list.
    - selectProject() sets currentProject (also persisted).
    - signOut() clears the localStorage payload.

  Tomorrow (post-security-adapter):
    - Replace this file with one that delegates to a real
      identity port (OAuth/SAML/magic-link/etc.). The shape +
      every consumer screen that reads it stays UNCHANGED.

  Project = topology `target.id`. createProject mints an id;
  effecting consumer actions thread that id through to the
  deployment dispatch as `{ id: project.id }`. The mapping is
  the only thing that needs to crystallize when real backends
  land.
-->
<script lang="ts" module>
  import { getContext } from "svelte";
  import type { User, Project } from "./types.js";

  export type { User, Project };
  export type { Membership } from "./types.js";

  export interface IdentityIOContext {
    /** The signed-in user, or null. */
    readonly user: User | null;
    /** Projects the user has access to. */
    readonly projects: readonly Project[];
    /** The currently selected project (route-driven; updated when
     *  the user navigates to /projects/:id). */
    readonly currentProject: Project | null;
    /** True once the IdentityIO has hydrated from localStorage AND
     *  (if a token was found) the /auth/me round-trip completed. */
    readonly ready: boolean;
    /** True after `signIn()` has been called — UI shows "check your
     *  email" until the user clicks the magic link and lands back. */
    readonly signInPending: boolean;
    /** Email the magic link was sent to (for the UI's "we sent
     *  the link to scott@…" message). */
    readonly signInPendingEmail: string | null;
    /** Error from the most recent signIn() attempt, if any. */
    readonly signInError: Error | null;
    /** Trigger the magic-link flow. POSTs to the deployment
     *  server's /auth/email/request; the magic link arrives by
     *  email (in dev: also logged to the server console as a
     *  devLink). Once the user clicks the link they're redirected
     *  back here and IdentityIO completes the sign-in via the
     *  #token hash fragment. */
    signIn(email: string): Promise<void>;
    /** Password sign-in — POSTs {email,password} to /auth/password; on success
     *  stores the JWT + hydrates the session. The allowlisted-email + shared-
     *  password bypass (no email round-trip). */
    signInWithPassword(email: string, password: string): Promise<void>;
    /** Reset the pending sign-in state — used by the SignInScreen's
     *  "use a different email" affordance. Does NOT invalidate any
     *  magic link already in flight; the link still works if clicked. */
    cancelSignIn(): void;
    /** Sign out + clear local state + JWT. */
    signOut(): Promise<void>;
    /** Set the active project by id (no-op if the id isn't in
     *  the user's projects list). */
    selectProject(id: string | null): void;
    /** Create a new project. Synthesises an id; appends to the
     *  user's projects; the user becomes its owner. */
    createProject(name: string): Promise<Project>;
  }

  export const IDENTITY_IO_KEY: unique symbol = Symbol("consumer.identity");

  /** localStorage key for cached user info (fast UI render on mount). */
  export const IDENTITY_STORAGE_KEY = "network:consumer:identity";
  /** localStorage key for the JWT (read by NetworkDeployment hyperedge). */
  export const AUTH_TOKEN_KEY = "network:consumer:auth";

  /** Default base for the deployment server's auth endpoints —
   *  mirrors NetworkDeployment hyperedge's endpoint resolver. */
  function resolveAuthBase(): string {
    if (typeof window === "undefined") return "https://network.liquidefy.io";
    const host = window.location.hostname;
    if (host === "localhost" || host === "127.0.0.1") {
      return "http://localhost:3500";
    }
    return "https://network.liquidefy.io";
  }

  export function useIdentityIO(): IdentityIOContext {
    const ctx = getContext<IdentityIOContext | undefined>(IDENTITY_IO_KEY);
    if (!ctx) {
      throw new Error(
        "useIdentityIO() called outside of an IdentityIO tree.",
      );
    }
    return ctx;
  }

  // ── Mock helpers (shared module-scope so they're stable) ──────
  function makeId(prefix: string): string {
    const r = Math.random().toString(36).slice(2, 10);
    const t = Date.now().toString(36);
    return `${prefix}-${t}-${r}`;
  }

  interface PersistedIdentity {
    readonly user: User;
    readonly projects: readonly Project[];
    readonly currentProjectId: string | null;
  }
</script>

<script lang="ts">
  import { setContext, type Snippet } from "svelte";

  interface Props {
    /** Override the auth base URL (defaults to localhost:3500 in dev,
     *  network.liquidefy.io in prod). */
    authBase?: string;
    onReady?: (ctx: IdentityIOContext) => void;
    children?: Snippet;
  }
  const { authBase = resolveAuthBase(), onReady, children }: Props = $props();

  // ─── Reactive state ──────────────────────────────────────────
  let user = $state<User | null>(null);
  let projects = $state<readonly Project[]>([]);
  let currentProject = $state<Project | null>(null);
  let ready = $state(false);
  let signInPending = $state(false);
  let signInPendingEmail = $state<string | null>(null);
  let signInError = $state<Error | null>(null);

  function readToken(): string | null {
    if (typeof localStorage === "undefined") return null;
    return localStorage.getItem(AUTH_TOKEN_KEY);
  }
  function writeToken(token: string | null): void {
    if (typeof localStorage === "undefined") return;
    if (token) localStorage.setItem(AUTH_TOKEN_KEY, token);
    else       localStorage.removeItem(AUTH_TOKEN_KEY);
  }

  function persist(): void {
    if (typeof localStorage === "undefined") return;
    if (!user) {
      localStorage.removeItem(IDENTITY_STORAGE_KEY);
      return;
    }
    const payload: PersistedIdentity = {
      user,
      projects,
      currentProjectId: currentProject?.id ?? null,
    };
    try {
      localStorage.setItem(IDENTITY_STORAGE_KEY, JSON.stringify(payload));
    } catch {
      /* swallow storage failures — session degrades to in-memory */
    }
  }

  // ── Hydrate on mount ─────────────────────────────────────────
  // Order:
  //   1. Check if the URL hash contains a fresh token (we just
  //      landed back from a magic-link verify). If so, stash it
  //      + clear the hash + load profile from /auth/me.
  //   2. Otherwise: load the cached user from localStorage (fast),
  //      then refresh against /auth/me in the background.
  //   3. Set ready = true when done.
  $effect(() => {
    void hydrate();
  });

  async function hydrate(): Promise<void> {
    if (typeof window === "undefined") {
      ready = true;
      return;
    }

    // 1. Catch the #token=… hash from the magic-link return.
    const hash = window.location.hash;
    const m = /#token=([^&]+)/.exec(hash);
    if (m && m[1]) {
      const token = decodeURIComponent(m[1]);
      writeToken(token);
      // Magic-link just landed → we're not in "check your email"
      // state anymore. Clear the pending flag so the SignInScreen
      // re-renders correctly (the auth guard will then push to
      // /projects once user is populated below).
      signInPending = false;
      signInPendingEmail = null;
      // Clear the hash so a refresh doesn't reprocess it.
      const cleanUrl =
        window.location.pathname + window.location.search;
      window.history.replaceState(null, "", cleanUrl || "/");
    }

    // 2. Cached user (fast path).
    try {
      const raw = localStorage.getItem(IDENTITY_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as PersistedIdentity;
        user = parsed.user;
        projects = parsed.projects;
        currentProject = parsed.projects.find(
          (p) => p.id === parsed.currentProjectId,
        ) ?? null;
      }
    } catch {
      try { localStorage.removeItem(IDENTITY_STORAGE_KEY); } catch { /**/ }
    }

    // 3. /auth/me round-trip — confirms the JWT is valid AND gets
    //    the canonical user data. If the token is rejected, we
    //    clear it and treat the session as signed-out.
    const token = readToken();
    if (token) {
      try {
        const res = await fetch(`${authBase}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json() as {
            userId: string; email: string; projectIds: readonly string[];
          };
          // Refresh / install user from server canonical data.
          const synthName = data.email.split("@")[0] ?? "user";
          user = {
            id: data.userId,
            email: data.email,
            displayName: user?.displayName ?? synthName,
            createdAtMs: user?.createdAtMs ?? Date.now(),
          };
          // Projects are managed by the deployment server in later
          // phases; for D1 we don't have project state from the
          // server yet, so keep whatever was cached locally.
          persist();
        } else {
          // 401 / 403 → token invalid. Clear.
          writeToken(null);
          user = null;
          persist();
        }
      } catch {
        // Network error — keep cached user, retry on next hydrate.
      }
    }

    ready = true;
  }

  const ctx: IdentityIOContext = {
    get user() { return user; },
    get projects() { return projects; },
    get currentProject() { return currentProject; },
    get ready() { return ready; },
    get signInPending() { return signInPending; },
    get signInPendingEmail() { return signInPendingEmail; },
    get signInError() { return signInError; },

    async signIn(email) {
      signInError = null;
      try {
        // Send the consumer's own origin as returnUrl so the verify
        // redirect comes back to the EXACT same origin (avoids
        // localhost vs 127.0.0.1 origin-mismatch killing the
        // localStorage handoff).
        const returnUrl = typeof window !== "undefined"
          ? `${window.location.origin}${window.location.pathname}`
          : undefined;
        const res = await fetch(`${authBase}/auth/email/request`, {
          method:  "POST",
          headers: { "Content-Type": "application/json" },
          body:    JSON.stringify({ email, returnUrl }),
        });
        if (!res.ok) {
          throw new Error(`sign-in failed: HTTP ${res.status}`);
        }
        // In dev mode the server returns devLink for convenience.
        // Don't auto-follow it — just surface a log so the operator
        // can copy-paste during testing.
        const data = await res.json() as { accepted: boolean; devLink?: string };
        if (data.devLink) {
          console.log(
            "[liquidefy] dev magic link (would normally arrive via email):",
            data.devLink,
          );
        }
        signInPending = true;
        signInPendingEmail = email;
      } catch (e) {
        signInError = e instanceof Error ? e : new Error(String(e));
        throw signInError;
      }
    },

    async signInWithPassword(email, password) {
      signInError = null;
      try {
        const res = await fetch(`${authBase}/auth/password`, {
          method:  "POST",
          headers: { "Content-Type": "application/json" },
          body:    JSON.stringify({ email, password }),
        });
        if (!res.ok) {
          throw new Error(
            res.status === 401
              ? "Invalid email or password."
              : `sign-in failed: HTTP ${res.status}`,
          );
        }
        const data = (await res.json()) as { token?: string };
        if (!data.token) throw new Error("sign-in failed: no token");
        writeToken(data.token);
        signInPending = false;
        signInPendingEmail = null;
        await hydrate();
      } catch (e) {
        signInError = e instanceof Error ? e : new Error(String(e));
        throw signInError;
      }
    },

    cancelSignIn() {
      signInPending = false;
      signInPendingEmail = null;
      signInError = null;
    },

    async signOut() {
      user = null;
      projects = [];
      currentProject = null;
      signInPending = false;
      signInPendingEmail = null;
      writeToken(null);
      persist();
    },

    selectProject(id) {
      // Guard: when there's no signed-in user, selectProject is a
      // no-op. ConsumerRoot calls this on every navigation; if we
      // wrote-through-to-persist while user is null, persist()
      // would removeItem and clobber any in-flight hydration —
      // the C2/C3 testing surfaced exactly this. Persist ONLY
      // when the user is set (the legitimate post-sign-in case).
      if (!user) return;
      if (id === null) {
        if (currentProject !== null) {
          currentProject = null;
          persist();
        }
        return;
      }
      const found = projects.find((p) => p.id === id);
      if (found && found.id !== currentProject?.id) {
        currentProject = found;
        persist();
      }
    },

    async createProject(name) {
      if (!user) {
        throw new Error("createProject: not signed in");
      }
      const project: Project = {
        id: makeId("proj"),
        name,
        ownerUserId: user.id,
        createdAtMs: Date.now(),
      };
      projects = [...projects, project];
      currentProject = project;
      persist();
      return project;
    },
  } satisfies IdentityIOContext;

  setContext(IDENTITY_IO_KEY, ctx);

  // Emit onReady once on the rising edge.
  let onReadyEmitted = false;
  $effect(() => {
    if (ready && !onReadyEmitted) {
      onReadyEmitted = true;
      onReady?.(ctx);
    }
  });
</script>

{@render children?.()}
